/**
 * OverrideHistoryTable - Reusable table component for displaying supervisor override history
 * Used in both the standalone Override History page and the Session Details modal tab
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import * as XLSX from 'xlsx-js-style';
import { useLanguage } from '../../../i18n/LanguageContext';
import {
  Download,
  ChevronUp,
  ChevronDown,
  Filter,
  X,
  RefreshCw,
  Calendar,
  User,
  Shield,
  FileText,
  Clock,
  Tag,
  ChevronLeft,
  ChevronRight,
  Factory,
  Printer
} from 'lucide-react';
import { formatGS1Barcode } from '../../../utils/barcodeUtils';
import DatePickerDropdown from '../../../components/DatePickerDropdown';
import api from '../../../services/api';

// Override record type from API
export interface OverrideRecord {
  id: string;
  session_number: string;
  sub_session_number: string | null;
  product_number: string;
  product_name: string;
  override_type: string;
  supervisor_name: string;
  operator_name: string | null;
  operator_employee_number: string | null;
  packer_name: string | null;
  packer_employee_number: string | null;
  station_name: string | null;
  reason: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

interface OverrideHistoryTableProps {
  // If provided, filters to specific session only (for Session Details tab)
  sessionId?: string;
  subSessionNumber?: string;
  // Whether to show session column (hide when embedded in session details)
  showSessionColumn?: boolean;
  // Whether to show filter controls
  showFilters?: boolean;
  // Compact mode for embedding in modals
  compact?: boolean;
}

// Simple inline barcode component for displaying GS1-128 barcodes
const InlineBarcode: React.FC<{ value: string }> = ({ value }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [error, setError] = useState(false);

  // Format the barcode value with GS1 AI parentheses for display
  const formattedValue = formatGS1Barcode(value);

  useEffect(() => {
    if (svgRef.current && value) {
      try {
        JsBarcode(svgRef.current, value, {
          format: 'CODE128',
          width: 1.5,
          height: 40,
          displayValue: false, // We'll show our own formatted text
          margin: 5
        });
        setError(false);
      } catch {
        setError(true);
      }
    }
  }, [value]);

  if (error || !value) {
    return <span className="font-mono text-xs break-all">{formattedValue || '-'}</span>;
  }

  return (
    <div className="flex flex-col items-start">
      <svg ref={svgRef} className="max-w-full" />
      <span className="font-mono text-xs text-gray-700 dark:text-gray-300 mt-1">{formattedValue}</span>
    </div>
  );
};

// Map override types to display labels and colors
const OVERRIDE_TYPE_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  label_reprint: { label: 'Label Reprint', color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  manual_weight: { label: 'Manual Weight', color: 'text-purple-700 dark:text-purple-300', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  yield_signoff: { label: 'Yield Sign-off', color: 'text-amber-700 dark:text-amber-300', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
  eod_signoff: { label: 'EOD Sign-off', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  session_completion: { label: 'Session Completion', color: 'text-red-700 dark:text-red-300', bgColor: 'bg-red-100 dark:bg-red-900/30' },
  hte_stock_edit: { label: 'HTE Stock', color: 'text-cyan-700 dark:text-cyan-300', bgColor: 'bg-cyan-100 dark:bg-cyan-900/30' }
};

const OverrideHistoryTable: React.FC<OverrideHistoryTableProps> = ({
  sessionId,
  subSessionNumber,
  showSessionColumn = true,
  showFilters = true,
  compact = false
}) => {
  const { t } = useLanguage();

  // Data state
  const [overrides, setOverrides] = useState<OverrideRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(compact ? 10 : 25);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [overrideType, setOverrideType] = useState('');
  const [supervisorFilter, setSupervisorFilter] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Sorting
  const [sortField, setSortField] = useState<'created_at' | 'override_type' | 'supervisor_name'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Expanded row for details
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  // Fetch override history
  const fetchOverrides = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number> = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (overrideType) params.override_type = overrideType;
      if (supervisorFilter) params.supervisor = supervisorFilter;
      if (sessionId) params.session_number = sessionId;
      if (subSessionNumber) params.sub_session_number = subSessionNumber;

      // Use the shared axios instance so the auth bearer token is attached
      // (the gateway rejects token-less requests with 401).
      const { data } = await api.get('/supervisor/override-history', { params });
      setOverrides(data.overrides);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, overrideType, supervisorFilter, sessionId, subSessionNumber, page, rowsPerPage]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  // Sort overrides client-side (API already returns sorted by date desc)
  const sortedOverrides = [...overrides].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';

    switch (sortField) {
      case 'created_at':
        aVal = new Date(a.created_at).getTime();
        bVal = new Date(b.created_at).getTime();
        break;
      case 'override_type':
        aVal = a.override_type;
        bVal = b.override_type;
        break;
      case 'supervisor_name':
        aVal = a.supervisor_name || '';
        bVal = b.supervisor_name || '';
        break;
    }

    if (sortDirection === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  });

  // Toggle sort
  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    // Helper to format details for export
    const formatDetailsForExport = (override: OverrideRecord): string => {
      const details = override.details;
      if (!details || Object.keys(details).length === 0) return '';

      switch (override.override_type) {
        case 'label_reprint':
          return details.box_number ? `Box ${details.box_number}` : '';
        case 'manual_weight':
          return details.new_mode ? `Mode: ${details.new_mode}` : '';
        case 'yield_signoff':
          return details.yield_percentage ? `Yield: ${Number(details.yield_percentage).toFixed(2)}%` : '';
        case 'hte_stock_edit':
          return details.item_number ? `${details.edit_target === 'buffer_delete' ? 'Delete' : 'Edit'} ${details.item_number}: ${details.old_value} → ${details.new_value}` : '';
        default:
          return '';
      }
    };

    const headers = [
      'Timestamp',
      'Override Type',
      'Supervisor',
      'Operator',
      'Packer',
      'Station',
      'Session',
      'Product #',
      'Product Name',
      'Details',
      'Reason'
    ];

    const rows = overrides.map(o => [
      new Date(o.created_at).toLocaleString(),
      OVERRIDE_TYPE_CONFIG[o.override_type]?.label || o.override_type,
      o.supervisor_name || '',
      o.operator_name || '',
      o.packer_name || '',
      o.station_name || '',
      o.sub_session_number || o.session_number || '',
      o.product_number || '',
      o.product_name || '',
      formatDetailsForExport(o),
      o.reason || ''
    ]);

    // Create worksheet with headers and data
    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Header style with blue background and white text
    const headerStyle = {
      fill: { fgColor: { rgb: '2563EB' } },
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        bottom: { style: 'thin', color: { rgb: '1D4ED8' } }
      }
    };

    // Apply header style to first row
    headers.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (ws[cellRef]) {
        ws[cellRef].s = headerStyle;
      }
    });

    // Calculate column widths based on content (auto-fit)
    const columnWidths = headers.map((header, colIndex) => {
      let maxLength = header.length;
      rows.forEach(row => {
        const cellLength = String(row[colIndex]).length;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      // Add some padding, min 10, max 50 characters
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });

    ws['!cols'] = columnWidths;

    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Override History');

    // Download file
    XLSX.writeFile(wb, `override-history-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Print report
  const handlePrint = () => {
    if (!overrides.length) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const tableRows = overrides.map(o => {
      const typeConfig = OVERRIDE_TYPE_CONFIG[o.override_type] || { label: o.override_type };

      // Format details for print
      let detailsText = '-';
      if (o.details && Object.keys(o.details).length > 0) {
        switch (o.override_type) {
          case 'label_reprint':
            detailsText = o.details.box_number ? `Box ${o.details.box_number}` : '-';
            break;
          case 'manual_weight':
            detailsText = o.details.new_mode ? `Mode: ${o.details.new_mode}` : '-';
            break;
          case 'yield_signoff':
            detailsText = o.details.yield_percentage ? `Yield: ${Number(o.details.yield_percentage).toFixed(2)}%` : '-';
            break;
          case 'session_completion':
            detailsText = o.details.early_completion_reason ? String(o.details.early_completion_reason) : '-';
            break;
          case 'hte_stock_edit':
            detailsText = o.details.item_number ? `${o.details.edit_target === 'buffer_delete' ? 'Delete' : 'Edit'} ${o.details.item_number}: ${o.details.old_value} → ${o.details.new_value}` : '-';
            break;
          default:
            detailsText = '-';
        }
      }

      return `
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">${new Date(o.created_at).toLocaleString()}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${typeConfig.label}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${o.supervisor_name || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${o.operator_name || o.operator_employee_number || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${o.packer_name || o.packer_employee_number || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${o.station_name || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${o.sub_session_number || o.session_number || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${detailsText}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${o.reason || '-'}</td>
        </tr>
      `;
    }).join('');

    // Build filters display
    const filterParts: string[] = [];
    if (dateFrom) filterParts.push(`<strong>From:</strong> ${dateFrom}`);
    if (dateTo) filterParts.push(`<strong>To:</strong> ${dateTo}`);
    if (overrideType) {
      const typeLabel = OVERRIDE_TYPE_CONFIG[overrideType]?.label || overrideType;
      filterParts.push(`<strong>Type:</strong> ${typeLabel}`);
    }
    if (supervisorFilter) filterParts.push(`<strong>Supervisor:</strong> ${supervisorFilter}`);
    const filtersHtml = filterParts.length > 0
      ? `<div class="filters">${filterParts.join(' &nbsp;|&nbsp; ')}</div>`
      : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Override History Report - ${new Date().toLocaleDateString()}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 10px; }
          .filters { margin-bottom: 10px; font-size: 12px; color: #666; }
          .summary { margin-bottom: 15px; font-size: 14px; }
          table { border-collapse: collapse; width: 100%; font-size: 11px; }
          th { background-color: #e5e7eb; color: #111; padding: 10px 8px; text-align: left; border: 1px solid #d1d5db; font-weight: 600; }
          td { border: 1px solid #ddd; padding: 8px; }
          tr:nth-child(even) { background-color: #f9fafb; }
          @media print {
            body { margin: 0; padding: 10px; }
            h1 { font-size: 14px; }
          }
        </style>
      </head>
      <body>
        <h1>Override History Report - ${new Date().toLocaleDateString()}</h1>
        ${filtersHtml}
        <div class="summary"><strong>Total Records:</strong> ${total}</div>
        <table>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Type</th>
              <th>Supervisor</th>
              <th>Operator</th>
              <th>Packer</th>
              <th>Station</th>
              <th>Session</th>
              <th>Details</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Clear filters
  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setOverrideType('');
    setSupervisorFilter('');
    setPage(0);
  };

  // Format timestamp
  const formatTimestamp = (ts: string) => {
    const date = new Date(ts);
    return date.toLocaleString();
  };

  // Format details for display based on override type
  const formatDetails = (override: OverrideRecord): string => {
    const details = override.details;
    if (!details || Object.keys(details).length === 0) return '-';

    switch (override.override_type) {
      case 'label_reprint':
        return details.box_number ? `Box ${details.box_number}` : '-';
      case 'manual_weight':
        return details.new_mode ? `Mode: ${details.new_mode}` : '-';
      case 'yield_signoff':
        return details.yield_percentage ? `Yield: ${Number(details.yield_percentage).toFixed(2)}%` : '-';
      case 'session_completion':
        return details.early_completion_reason ? String(details.early_completion_reason) : '-';
      case 'hte_stock_edit':
        return details.item_number ? `${details.edit_target === 'buffer_delete' ? 'Delete' : 'Edit'} ${details.item_number}: ${details.old_value} → ${details.new_value}` : '-';
      default:
        return '-';
    }
  };

  // Render formatted expanded details based on override type
  const renderExpandedDetails = (override: OverrideRecord) => {
    const details = override.details;
    if (!details || Object.keys(details).length === 0) {
      return <p className="text-gray-500 dark:text-gray-400 text-sm">No additional details</p>;
    }

    switch (override.override_type) {
      case 'session_completion':
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            {'early_completion_reason' in details && details.early_completion_reason != null ? (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Reason:</span>
                <span className="ml-2 text-gray-900 dark:text-white font-medium capitalize">
                  {String(details.early_completion_reason).replace(/_/g, ' ')}
                </span>
              </div>
            ) : null}
            {'operator_name' in details && details.operator_name != null ? (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Operator:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{String(details.operator_name)}</span>
              </div>
            ) : null}
            {'station_name' in details && details.station_name != null ? (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Station:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{String(details.station_name)}</span>
              </div>
            ) : null}
            {'progress_percentage' in details && details.progress_percentage !== undefined ? (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Progress:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{Number(details.progress_percentage).toFixed(2)}%</span>
              </div>
            ) : null}
            {'actual_yield' in details && details.actual_yield !== undefined ? (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Actual Yield:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{Number(details.actual_yield).toFixed(2)}%</span>
              </div>
            ) : null}
            {'expected_yield' in details && details.expected_yield !== undefined ? (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Expected Yield:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{Number(details.expected_yield).toFixed(2)}%</span>
              </div>
            ) : null}
          </div>
        );
      case 'label_reprint':
        return (
          <div className="text-sm space-y-3">
            {/* Product and Box info row */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 items-center">
              {'box_number' in details && details.box_number != null ? (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Box #:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{String(details.box_number)}</span>
                </div>
              ) : null}
              {'product_number' in details && details.product_number != null ? (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Product #:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{String(details.product_number)}</span>
                </div>
              ) : null}
              {'product_name' in details && details.product_name != null ? (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Product:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{String(details.product_name)}</span>
                </div>
              ) : null}
            </div>
            {/* Barcode displayed as GS1-128 format */}
            {'barcode' in details && details.barcode != null ? (
              <div className="pt-2">
                <InlineBarcode value={String(details.barcode)} />
              </div>
            ) : null}
          </div>
        );
      case 'yield_signoff':
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            {'yield_percentage' in details && details.yield_percentage !== undefined ? (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Yield:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{Number(details.yield_percentage).toFixed(2)}%</span>
              </div>
            ) : null}
            {'variance' in details && details.variance !== undefined ? (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Variance:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{Number(details.variance).toFixed(2)}%</span>
              </div>
            ) : null}
          </div>
        );
      default:
        // Fallback to formatted key-value pairs
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            {Object.entries(details).map(([key, value]) => (
              <div key={key}>
                <span className="text-gray-500 dark:text-gray-400 capitalize">{key.replace(/_/g, ' ')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            ))}
          </div>
        );
    }
  };

  // Render sort icon
  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const hasActiveFilters = dateFrom || dateTo || overrideType || supervisorFilter;

  return (
    <div className={`flex flex-col ${compact ? 'h-full' : ''}`}>
      {/* Header with filters and export */}
      {showFilters && (
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Filter className="w-4 h-4 text-gray-500" />
              {t('common.filters') || 'Filters'}
              {hasActiveFilters && (
                <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {[dateFrom, dateTo, overrideType, supervisorFilter].filter(Boolean).length}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <span className="text-gray-400">×</span>
                {t('common.clear') || 'Clear'}
              </button>
            )}

            <button
              onClick={fetchOverrides}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              title={t('common.refresh') || 'Refresh'}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              disabled={overrides.length === 0}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Printer className="w-4 h-4 mr-2" />
              {t('common.print') || 'Print'}
            </button>
            <button
              onClick={exportToExcel}
              disabled={overrides.length === 0}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.export') || 'Export'}
            </button>
          </div>
        </div>
      )}

      {/* Filter Panel */}
      {showFilters && showFilterPanel && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('supervisor.dateFrom') || 'Date From'}
              </label>
              <DatePickerDropdown
                selected={dateFrom || null}
                onSelect={(date) => { setDateFrom(date); setPage(0); }}
              >
                {({ onClick }) => (
                  <button type="button" onClick={onClick} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                    {dateFrom ? (() => { const d = new Date(dateFrom + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                  </button>
                )}
              </DatePickerDropdown>
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('supervisor.dateTo') || 'Date To'}
              </label>
              <DatePickerDropdown
                selected={dateTo || null}
                onSelect={(date) => { setDateTo(date); setPage(0); }}
              >
                {({ onClick }) => (
                  <button type="button" onClick={onClick} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                    {dateTo ? (() => { const d = new Date(dateTo + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                  </button>
                )}
              </DatePickerDropdown>
            </div>

            {/* Override Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Tag className="w-4 h-4 inline mr-1" />
                {t('supervisor.overrideType') || 'Override Type'}
              </label>
              <select
                value={overrideType}
                onChange={(e) => { setOverrideType(e.target.value); setPage(0); }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">{t('common.all') || 'All'}</option>
                {Object.entries(OVERRIDE_TYPE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Supervisor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Shield className="w-4 h-4 inline mr-1" />
                {t('supervisor.supervisor') || 'Supervisor'}
              </label>
              <input
                type="text"
                value={supervisorFilter}
                onChange={(e) => { setSupervisorFilter(e.target.value); setPage(0); }}
                placeholder={t('supervisor.searchSupervisor') || 'Search...'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-8 text-center text-gray-500 dark:text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          {t('common.loading') || 'Loading...'}
        </div>
      )}

      {/* Empty state */}
      {!loading && sortedOverrides.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-8 text-center text-gray-500 dark:text-gray-400">
          {t('supervisor.noOverridesFound') || 'No overrides found'}
        </div>
      )}

      {/* Mobile Card Layout */}
      {!loading && sortedOverrides.length > 0 && (
        <div className="md:hidden space-y-3">
          {sortedOverrides.map((override) => {
            const typeConfig = OVERRIDE_TYPE_CONFIG[override.override_type] || {
              label: override.override_type,
              color: 'text-gray-700 dark:text-gray-300',
              bgColor: 'bg-gray-100 dark:bg-gray-800'
            };
            const isExpanded = expandedRowId === override.id;

            return (
              <div
                key={override.id}
                className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-pointer ${isExpanded ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setExpandedRowId(isExpanded ? null : override.id)}
              >
                {/* Header row: Type badge and timestamp */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeConfig.bgColor} ${typeConfig.color}`}>
                    {typeConfig.label}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatTimestamp(override.created_at)}
                  </span>
                </div>

                {/* Main content */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('supervisor.supervisor') || 'Supervisor'}:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{override.supervisor_name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('supervisor.operator') || 'Operator'}:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{override.operator_name || override.operator_employee_number || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('supervisor.packer') || 'Packer'}:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{override.packer_name || override.packer_employee_number || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Factory className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('supervisor.station') || 'Station'}:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{override.station_name || '-'}</span>
                  </div>
                  {showSessionColumn && (
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('supervisor.session') || 'Session'}:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {override.sub_session_number || override.session_number || '-'}
                      </span>
                    </div>
                  )}
                  {override.reason && (
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400">{override.reason}</p>
                    </div>
                  )}
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className="font-medium text-gray-700 dark:text-gray-300 mb-3 text-sm">
                      {t('supervisor.additionalDetails') || 'Additional Details'}
                    </p>
                    {renderExpandedDetails(override)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop Table Layout */}
      {!loading && sortedOverrides.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className={`hidden md:block ${compact ? 'max-h-[400px]' : ''}`}>
          <div className="overflow-x-auto">
          <table className="w-max min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 whitespace-nowrap"
                  onClick={() => handleSort('created_at')}
                >
                  <Clock className="w-4 h-4 inline mr-1" />
                  {t('supervisor.timestamp') || 'Timestamp'}
                  <SortIcon field="created_at" />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 whitespace-nowrap"
                  onClick={() => handleSort('override_type')}
                >
                  <Tag className="w-4 h-4 inline mr-1" />
                  {t('supervisor.type') || 'Type'}
                  <SortIcon field="override_type" />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 whitespace-nowrap"
                  onClick={() => handleSort('supervisor_name')}
                >
                  <Shield className="w-4 h-4 inline mr-1" />
                  {t('supervisor.supervisor') || 'Supervisor'}
                  <SortIcon field="supervisor_name" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  <User className="w-4 h-4 inline mr-1" />
                  {t('supervisor.operator') || 'Operator'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  <User className="w-4 h-4 inline mr-1" />
                  {t('supervisor.packer') || 'Packer'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  <Factory className="w-4 h-4 inline mr-1" />
                  {t('supervisor.station') || 'Station'}
                </th>
                {showSessionColumn && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                    <FileText className="w-4 h-4 inline mr-1" />
                    {t('supervisor.session') || 'Session'}
                  </th>
                )}
                {!compact && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                    {t('supervisor.details') || 'Details'}
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  {t('supervisor.reason') || 'Reason'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedOverrides.map((override) => {
                const typeConfig = OVERRIDE_TYPE_CONFIG[override.override_type] || {
                  label: override.override_type,
                  color: 'text-gray-700 dark:text-gray-300',
                  bgColor: 'bg-gray-100 dark:bg-gray-800'
                };
                const isExpanded = expandedRowId === override.id;

                return (
                  <React.Fragment key={override.id}>
                    <tr
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${isExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                      onClick={() => setExpandedRowId(isExpanded ? null : override.id)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {formatTimestamp(override.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeConfig.bgColor} ${typeConfig.color}`}>
                          {typeConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {override.supervisor_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {override.operator_name || override.operator_employee_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {override.packer_name || override.packer_employee_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {override.station_name || '-'}
                      </td>
                      {showSessionColumn && (
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                          {override.sub_session_number || override.session_number || '-'}
                        </td>
                      )}
                      {!compact && (
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                          {formatDetails(override)}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {override.reason || '-'}
                      </td>
                    </tr>
                    {/* Expanded details row */}
                    {isExpanded && (
                      <tr className="bg-blue-50 dark:bg-blue-900/20">
                        <td colSpan={showSessionColumn ? (compact ? 8 : 9) : (compact ? 7 : 8)} className="px-4 py-3">
                          <div className="text-sm">
                            <p className="font-medium text-gray-700 dark:text-gray-300 mb-3">
                              {t('supervisor.additionalDetails') || 'Additional Details'}
                            </p>
                            {renderExpandedDetails(override)}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {`${t('common.showing') || 'Showing'} ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, total)} ${t('common.of') || 'of'} ${total}`}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="p-2 text-gray-700 dark:text-gray-200 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('supervisor.page') || 'Page'} {page + 1} / {Math.ceil(total / rowsPerPage) || 1}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * rowsPerPage >= total}
            className="p-2 text-gray-700 dark:text-gray-200 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      </div>
      )}
    </div>
  );
};

export default OverrideHistoryTable;
